import { useState, useEffect, useCallback } from "react";
import {
  HiOutlineMap, HiOutlineMagnifyingGlass, HiOutlineCheckCircle,
  HiOutlineXCircle, HiOutlineClock, HiOutlineChevronDown,
  HiOutlineChevronLeft, HiOutlineChevronRight, HiOutlineTrash,
  HiOutlineEye, HiOutlineArrowTrendingUp,
} from "react-icons/hi2";
import AdminLayout from "./AdminLayout";
import { storiesAPI } from "../services/api";
import toast from "react-hot-toast";
import "./AdminStories.css";

const CAT_COLORS = {
  veteran:         "#5f7158",
  first_responder: "#a86642",
  law_enforcement: "#4a6b8a",
  gold_star:       "#8a7a42",
  volunteer:       "#7a5a8a",
  donor:           "#8a4a5a",
};
const CAT_LABELS = {
  veteran:"Veteran", first_responder:"First Responder",
  law_enforcement:"Law Enforcement", gold_star:"Gold Star",
  volunteer:"Volunteer", donor:"Donor",
};

const STATUS_CFG = {
  pending:  { cls:"ast__badge--pending",  Icon:HiOutlineClock,         label:"Pending"  },
  approved: { cls:"ast__badge--approved", Icon:HiOutlineCheckCircle,   label:"Approved" },
  rejected: { cls:"ast__badge--rejected", Icon:HiOutlineXCircle,       label:"Rejected" },
};

function StarRow({ rating }) {
  return (
    <span className="ast__stars">
      {[1,2,3,4,5].map(n => <span key={n} className={n <= rating ? "ast__star--on" : "ast__star--off"}>★</span>)}
    </span>
  );
}

function DetailPanel({ story, onClose, onApprove, onReject, onDelete, saving }) {
  if (!story) return null;
  const cfg = STATUS_CFG[story.status] || STATUS_CFG.pending;
  return (
    <div className="ast__detail">
      <div className="ast__detail-head">
        <h3 className="ast__detail-title">Story Details</h3>
        <button type="button" className="ast__detail-close" onClick={onClose}>✕</button>
      </div>

      <div className="ast__detail-hero" style={{ borderLeft:`3px solid ${CAT_COLORS[story.category] || "#6b6258"}` }}>
        <p className="ast__detail-name">{story.first_name}{story.last_name_initial ? ` ${story.last_name_initial}.` : ""}</p>
        <div className="ast__detail-meta">
          <span className="ast__cat-pill" style={{ background:`${CAT_COLORS[story.category]}20`, color:CAT_COLORS[story.category] }}>
            {CAT_LABELS[story.category] || story.category}
          </span>
          <span className={`ast__badge ${cfg.cls}`}><cfg.Icon /> {cfg.label}</span>
        </div>
        <p className="ast__detail-location">📍 {story.city}{story.country && story.country !== "USA" ? `, ${story.country}` : ""}</p>
        {story.retreat_name && <p className="ast__detail-retreat">🏕 {story.retreat_name}</p>}
        <StarRow rating={story.rating} />
      </div>

      <div className="ast__detail-quote">
        <p className="ast__detail-quote-text">"{story.quote}"</p>
      </div>

      <div className="ast__detail-coords">
        <span>Lat: {parseFloat(story.lat).toFixed(4)}</span>
        <span>Lng: {parseFloat(story.lng).toFixed(4)}</span>
      </div>

      <div className="ast__detail-actions">
        {story.status !== "approved" && (
          <button type="button" className="ast__approve-btn" disabled={saving}
            onClick={() => onApprove(story.id)}>
            <HiOutlineCheckCircle /> {saving ? "Saving…" : "Approve & Publish"}
          </button>
        )}
        {story.status !== "rejected" && (
          <button type="button" className="ast__reject-btn" disabled={saving}
            onClick={() => onReject(story.id)}>
            <HiOutlineXCircle /> Reject
          </button>
        )}
        {story.status === "approved" && (
          <button type="button" className="a-btn a-btn--outline" disabled={saving}
            onClick={() => onApprove(story.id)} style={{ width:"100%" }}>
            Already Approved ✓
          </button>
        )}
        <button type="button" className="ast__delete-btn"
          onClick={() => {
              toast((t) => (
                <div style={{ display:"flex", flexDirection:"column", gap:"0.5rem" }}>
                  <p style={{ margin:0, fontWeight:700 }}>Delete this story?</p>
                  <p style={{ margin:0, fontSize:"0.78rem", color:"#6b6258" }}>This cannot be undone.</p>
                  <div style={{ display:"flex", gap:"0.5rem" }}>
                    <button onClick={() => { toast.dismiss(t.id); onDelete(story.id); }}
                      style={{ background:"#dc2626", color:"#fff", border:"none", borderRadius:"5px", padding:"0.35rem 0.75rem", cursor:"pointer", fontWeight:700, fontSize:"0.78rem" }}>Delete</button>
                    <button onClick={() => toast.dismiss(t.id)}
                      style={{ background:"#f3f4f0", border:"none", borderRadius:"5px", padding:"0.35rem 0.75rem", cursor:"pointer", fontSize:"0.78rem" }}>Cancel</button>
                  </div>
                </div>
              ), { duration: Infinity, style: { maxWidth:"320px" } });
            }}>
          <HiOutlineTrash /> Delete
        </button>
      </div>
    </div>
  );
}

export default function AdminStories() {
  const [stories, setStories]   = useState([]);
  const [summary, setSummary]   = useState({});
  const [loading, setLoading]   = useState(true);
  const [saving, setSaving]     = useState(false);
  const [selected, setSelected] = useState(null);
  const [search, setSearch]     = useState("");
  const [statusFilter, setStatusF] = useState("all");
  const [catFilter, setCat]     = useState("all");
  const [page, setPage]         = useState(1);
  const [total, setTotal]       = useState(0);


  const fetchStories = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, limit:10 };
      if (statusFilter !== "all") params.status   = statusFilter;
      if (catFilter    !== "all") params.category = catFilter;
      if (search)                 params.search   = search;
      const res = await storiesAPI.adminList(params);
      setStories(res.data || []);
      setTotal(res.pagination?.total || 0);
      setSummary(res.summary || {});
    } catch (err) { console.error(err.message); }
    finally { setLoading(false); }
  }, [page, statusFilter, catFilter, search]);

  useEffect(() => { fetchStories(); }, [fetchStories]);

  const handleStatusChange = async (id, status) => {
    setSaving(true);
    const tid = toast.loading(status === "approved" ? "Approving story…" : "Rejecting story…");
    try {
      await storiesAPI.updateStatus(id, status);
      toast.success(status === "approved" ? "Story approved and published to the map!" : "Story rejected.", { id: tid });
      fetchStories();
      setSelected(prev => prev?.id === id ? { ...prev, status } : prev);
    } catch (err) { toast.error("Failed: " + err.message, { id: tid }); }
    finally { setSaving(false); }
  };

  const handleDelete = async (id) => {
    try {
      await storiesAPI.delete(id);
      toast.success("Story deleted.");
      fetchStories();
      setSelected(null);
    } catch (err) { toast.error("Failed: " + err.message); }
  };

  const totalPages = Math.max(1, Math.ceil(total / 10));

  const STAT_CARDS = [
    { label:"Total Stories",  value:summary.total    ?? 0, color:"#e05a2b", bg:"rgba(var(--admin-rust-rgb), 0.1)",   Icon:HiOutlineMap, onClick:() => { setStatusF("all"); setPage(1); } },
    { label:"Pending Review", value:summary.pending  ?? 0, color:"#d97706", bg:"rgba(217,119,6,0.1)",   Icon:HiOutlineClock, onClick:() => { setStatusF("pending"); setPage(1); } },
    { label:"Approved",       value:summary.approved ?? 0, color:"#16a34a", bg:"rgba(22,163,74,0.1)",   Icon:HiOutlineCheckCircle, onClick:() => { setStatusF("approved"); setPage(1); } },
    { label:"Rejected",       value:summary.rejected ?? 0, color:"#dc2626", bg:"rgba(220,38,38,0.1)",   Icon:HiOutlineXCircle, onClick:() => { setStatusF("rejected"); setPage(1); } },
  ];

  return (
    <AdminLayout pageTitle="Community Stories" pageSubtitle="Review and approve stories for the community impact map." permission="stories.view">

      {/* Stat cards */}
      <div className="ast__stats-grid">
        {STAT_CARDS.map(s => (
          <div key={s.label}
            className={[
              "acard ast__stat-card",
              s.onClick ? "ast__stat-card--clickable" : "",
              (s.label === "Pending Review" && statusFilter === "pending") ? "ast__stat-card--active" : "",
              (s.label === "Approved"       && statusFilter === "approved") ? "ast__stat-card--active" : "",
              (s.label === "Rejected"       && statusFilter === "rejected") ? "ast__stat-card--active" : "",
              (s.label === "Total Stories"  && statusFilter === "all")      ? "ast__stat-card--active" : "",
            ].join(" ")}
            onClick={s.onClick}>
            <div className="ast__stat-top">
              <div className="ast__stat-icon" style={{ background:s.bg }}>
                <s.Icon style={{ color:s.color, fontSize:"1.1rem" }} />
              </div>
              <p className="ast__stat-label">{s.label}</p>
            </div>
            <p className="ast__stat-value">{s.value}</p>
            <p className="ast__stat-change" style={{ color:"#9ca3af" }}>
              {s.label === "Pending Review" && s.value > 0 ? <><HiOutlineArrowTrendingUp style={{ color:"#d97706" }}/> Needs attention</> : "Live count"}
            </p>
          </div>
        ))}
      </div>

      <div className={`ast__layout ${selected ? "ast__layout--detail" : ""}`}>
        <div className="ast__table-panel">
          {/* Filters */}
          <div className="ast__filters">
            <div className="asu__search-wrap">
              <HiOutlineMagnifyingGlass className="asu__search-icon" />
              <input type="text" className="asu__search" placeholder="Search stories, names, cities…"
                value={search} onChange={e => { setSearch(e.target.value); setPage(1); }} />
            </div>
            <div className="aap__select-wrap">
              <select className="aap__filter-select" value={statusFilter}
                onChange={e => { setStatusF(e.target.value); setPage(1); }}>
                <option value="all">All Statuses</option>
                <option value="pending">Pending</option>
                <option value="approved">Approved</option>
                <option value="rejected">Rejected</option>
              </select>
              <HiOutlineChevronDown className="aap__select-icon" />
            </div>
            <div className="aap__select-wrap">
              <select className="aap__filter-select" value={catFilter}
                onChange={e => { setCat(e.target.value); setPage(1); }}>
                <option value="all">All Categories</option>
                <option value="veteran">Veterans</option>
                <option value="first_responder">First Responders</option>
                <option value="law_enforcement">Law Enforcement</option>
                <option value="gold_star">Gold Star</option>
                <option value="volunteer">Volunteers</option>
                <option value="donor">Donors</option>
              </select>
              <HiOutlineChevronDown className="aap__select-icon" />
            </div>
          </div>

          {/* Table */}
          <div className="acard aap__table-card">
            <table className="aap__table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Category</th>
                  <th>Location</th>
                  <th>Rating</th>
                  <th>Quote</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr><td colSpan="7" style={{ textAlign:"center", padding:"3rem", color:"#9ca3af" }}>Loading stories…</td></tr>
                ) : stories.length === 0 ? (
                  <tr><td colSpan="7" style={{ textAlign:"center", padding:"3rem", color:"#9ca3af" }}>No stories found.</td></tr>
                ) : stories.map(s => {
                  const cfg = STATUS_CFG[s.status] || STATUS_CFG.pending;
                  return (
                    <tr key={s.id}
                      className={`aap__row ${selected?.id===s.id ? "aap__row--selected" : ""}`}
                      onClick={() => setSelected(s)}>
                      <td>
                        <p className="aap__applicant-name">
                          {s.first_name}{s.last_name_initial ? ` ${s.last_name_initial}.` : ""}
                        </p>
                        <p className="aap__applicant-email">{s.city}</p>
                      </td>
                      <td>
                        <span className="ast__cat-pill"
                          style={{ background:`${CAT_COLORS[s.category] || "#6b6258"}20`, color:CAT_COLORS[s.category] || "#6b6258" }}>
                          {CAT_LABELS[s.category] || s.category}
                        </span>
                      </td>
                      <td className="aap__applicant-email">{s.city}{s.country && s.country !== "USA" ? `, ${s.country}` : ""}</td>
                      <td><StarRow rating={s.rating} /></td>
                      <td className="ast__quote-cell">"{s.quote.slice(0,60)}{s.quote.length>60?"…":""}"</td>
                      <td><span className={`ast__badge ${cfg.cls}`}><cfg.Icon /> {cfg.label}</span></td>
                      <td onClick={e => e.stopPropagation()}>
                        <div className="aap__row-actions">
                          <button type="button" className="a-btn a-btn--ghost aap__action-btn"
                            onClick={() => setSelected(s)} title="View">
                            <HiOutlineEye />
                          </button>
                          {s.status !== "approved" && (
                            <button type="button" className="ast__quick-approve" disabled={saving}
                              onClick={() => handleStatusChange(s.id, "approved")} title="Approve">
                              <HiOutlineCheckCircle />
                            </button>
                          )}
                          {s.status !== "rejected" && (
                            <button type="button" className="ast__quick-reject" disabled={saving}
                              onClick={() => handleStatusChange(s.id, "rejected")} title="Reject">
                              <HiOutlineXCircle />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>

            {/* Pagination */}
            <div className="aap__pagination">
              <p className="aap__pagination-info">
                {total > 0 ? `Showing ${((page-1)*10)+1}–${Math.min(page*10,total)} of ${total} stories` : "No stories"}
              </p>
              <div className="aap__pagination-pages">
                <button type="button" className="aap__page-btn" disabled={page===1} onClick={() => setPage(p=>p-1)}><HiOutlineChevronLeft /></button>
                {Array.from({length:Math.min(5,totalPages)},(_,i)=>i+1).map(p=>(
                  <button key={p} type="button" className={`aap__page-btn ${page===p?"aap__page-btn--active":""}`} onClick={()=>setPage(p)}>{p}</button>
                ))}
                <button type="button" className="aap__page-btn" disabled={page>=totalPages} onClick={()=>setPage(p=>p+1)}><HiOutlineChevronRight /></button>
              </div>
              <div className="aap__select-wrap">
                <select className="aap__page-size"><option>10 / page</option><option>20 / page</option></select>
                <HiOutlineChevronDown className="aap__select-icon" />
              </div>
            </div>
          </div>
        </div>

        {selected && (
          <DetailPanel
            story={selected}
            onClose={() => setSelected(null)}
            onApprove={(id) => handleStatusChange(id, "approved")}
            onReject={(id)  => handleStatusChange(id, "rejected")}
            onDelete={handleDelete}
            saving={saving}
          />
        )}
      </div>
    </AdminLayout>
  );
}
